import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, UploadSimple, CheckCircle, Warning, FileArrowDown } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { zar } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { MatchAssign, StockItemForm, BASE_UNITS, lowConf } from "../components/MatchAssign";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_MAPPING = { description: "", purchase_unit: "", pack_size: "", pack_unit: "", price: "" };
const MAPPING_FIELDS = [["description", "Description (required)"], ["purchase_unit", "Purchase unit"],
  ["pack_size", "Pack size"], ["pack_unit", "Pack unit"], ["price", "List price"]];
const EMPTY_ITEM = { name: "", base_unit: "unit", category_id: "" };
const STATUS_LABELS = { mapping: "Needs mapping", extracting: "Extracting…", pending_review: "In review",
  confirmed: "Confirmed", failed: "Failed" };

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export default function SupplierCatalog() {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [imports, setImports] = useState([]);
  const [stats, setStats] = useState(null);
  const [stockItems, setStockItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stage, setStage] = useState("idle");
  const [current, setCurrent] = useState(null);
  const [mapping, setMapping] = useState(EMPTY_MAPPING);
  const [rows, setRows] = useState([]);
  const [threshold, setThreshold] = useState("90");
  const [bulkCat, setBulkCat] = useState("");
  const [busy, setBusy] = useState(false);
  const [newItemFor, setNewItemFor] = useState(null);
  const [newItem, setNewItem] = useState(EMPTY_ITEM);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(() => {
    api.get("/suppliers?all=true").then((r) => setSupplier(r.data.find((s) => s.id === supplierId) || null)).catch(() => toast.error("Failed to load supplier"));
    api.get(`/catalog-imports?supplier_id=${supplierId}`).then((r) => setImports(r.data)).catch(() => {});
    api.get("/catalog-imports/stats").then((r) => setStats(r.data.find((s) => s.supplier_id === supplierId) || null)).catch(() => {});
    api.get("/stock-items").then((r) => setStockItems(r.data)).catch(() => {});
    api.get("/invoice-categories").then((r) => setCategories(r.data)).catch(() => {});
  }, [supplierId]);
  useEffect(() => { load(); return () => clearTimeout(pollRef.current); }, [load]);

  const poll = (id) => {
    pollRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/catalog-imports/${id}`);
        if (data.status === "extracting") return poll(id);
        handleImport(data);
        load();
      } catch { setStage("idle"); }
    }, 2500);
  };

  const startReview = (imp) => {
    setCurrent(imp);
    setRows((imp.rows || []).map((r) => ({
      ...r,
      selected: r.match_state === "already_mapped" ? !!r.price_changed : true,
      create: r.match_state === "none",
      new_name: r.raw_description,
      new_unit: r.inferred_base_unit || (BASE_UNITS.includes(r.pack_unit) ? r.pack_unit : "unit"),
      new_category_id: r.inferred_category_id || "",
    })));
    setStage("review");
  };

  const handleImport = (imp) => {
    setCurrent(imp);
    if (imp.status === "mapping") { setMapping(EMPTY_MAPPING); setStage("mapping"); }
    else if (imp.status === "extracting") { setStage("extracting"); poll(imp.id); }
    else if (imp.status === "pending_review") startReview(imp);
    else if (imp.status === "failed") { toast.error(imp.error || "Extraction failed"); setStage("idle"); }
    else setStage("idle");
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("supplier_id", supplierId);
      const { data } = await api.post("/catalog-imports", fd);
      handleImport(data);
      load();
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submitMapping = async () => {
    if (!mapping.description) return toast.error("Map the description column");
    setBusy(true);
    try {
      const { data } = await api.post(`/catalog-imports/${current.id}/mapping`, { mapping });
      toast.success("Mapping saved — it will be remembered for this supplier");
      startReview(data);
      load();
    } catch (e) { toast.error(apiError(e, "Mapping failed")); }
    finally { setBusy(false); }
  };

  const patchRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const toggleAll = (checked) => setRows((rs) => rs.map((r) => ({ ...r, selected: checked })));
  const acceptMatched = () => setRows((rs) => rs.map((r) => (r.stock_item_id ? { ...r, selected: true } : r)));
  const acceptSuggestions = () => {
    const t = Number(threshold) || 0;
    setRows((rs) => rs.map((r) => {
      const top = (r.candidates || [])[0];
      if (!r.stock_item_id && top && top.score >= t) {
        const it = stockItems.find((x) => x.id === top.stock_item_id);
        return { ...r, stock_item_id: top.stock_item_id, stock_item_name: it?.name || top.name, selected: true, create: false };
      }
      return r;
    }));
  };
  const applyCategory = () => {
    if (!bulkCat) return;
    setRows((rs) => rs.map((r) => (r.selected && r.create && !r.stock_item_id ? { ...r, new_category_id: bulkCat } : r)));
  };

  const createItem = async () => {
    if (!newItem.name.trim()) return toast.error("Item name required");
    try {
      const { data } = await api.post("/stock-items", { ...newItem, category_id: newItem.category_id || null });
      setStockItems((s) => [...s, data].sort((a, b) => a.name.localeCompare(b.name)));
      patchRow(newItemFor, { stock_item_id: data.id, stock_item_name: data.name, create: false, selected: true });
      setNewItemFor(null); setNewItem(EMPTY_ITEM);
      toast.success(`Stock item '${data.name}' created`);
    } catch (e) { toast.error(apiError(e, "Create failed")); }
  };

  const confirm = async () => {
    const actionable = rows.filter((r) => r.selected && (r.stock_item_id || r.create));
    if (!actionable.length) return toast.error("Nothing selected to confirm");
    setBusy(true);
    try {
      const payload = {
        rows: rows.map((r) => ({
          id: r.id, raw_description: r.raw_description,
          purchase_unit: r.purchase_unit || null, pack_size: num(r.pack_size), pack_unit: r.pack_unit || null,
          list_price: num(r.list_price),
          stock_item_id: r.selected ? (r.stock_item_id || null) : null,
          new_stock_item: r.selected && !r.stock_item_id && r.create
            ? { name: (r.new_name || "").trim(), base_unit: r.new_unit, category_id: r.new_category_id || null } : null,
          skipped: !r.selected || (!r.stock_item_id && !r.create),
        })),
      };
      const { data } = await api.post(`/catalog-imports/${current.id}/confirm`, payload);
      toast.success(`Catalog confirmed — ${data.matched_count} matched, ${data.created_count} new items, ${data.updated_count} updated`);
      setStage("idle"); setCurrent(null); setRows([]);
      load();
    } catch (e) { toast.error(apiError(e, "Confirm failed")); }
    finally { setBusy(false); }
  };

  const stateChip = (r) => {
    if (r.match_state === "already_mapped") {
      return r.price_changed
        ? <Badge variant="outline" className="text-[#F4A261] border-[#F4A261]/40" data-testid="catalog-chip-price">Price changed</Badge>
        : <Badge variant="outline" className="text-[#A9C97E] border-[#4A5D23]/40">Already mapped</Badge>;
    }
    if (r.match_state === "candidate")
      return <Badge variant="outline" className="text-[#F4A261] border-[#F4A261]/40">Suggestion</Badge>;
    return <Badge variant="outline" className="text-[#F87171] border-[#B33939]/40"><Warning size={11} className="mr-1" />No match</Badge>;
  };

  const selectedCount = rows.filter((r) => r.selected && (r.stock_item_id || r.create)).length;

  return (
    <div className="space-y-6" data-testid="catalog-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" data-testid="catalog-back-btn" onClick={() => navigate("/suppliers")}>
            <ArrowLeft size={15} className="mr-1" /> Suppliers
          </Button>
          <h1 className="font-heading text-3xl font-bold mt-1">{supplier ? supplier.name : "…"} — price list catalog</h1>
          <p className="text-muted-foreground mt-1">
            {stats && stats.last_import_date
              ? `${stats.pct_aliased ?? 0}% of last catalog aliased · ${stats.total_aliases} aliases on record · last import ${stats.last_import_date.slice(0, 10)}`
              : "No catalog imported yet — import the price list once so invoices auto-match."}
          </p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.pdf,.jpg,.jpeg,.png,.webp" className="hidden"
            data-testid="catalog-file-input" onChange={(e) => upload(e.target.files?.[0])} />
          <Button data-testid="catalog-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}
            className="bg-primary hover:bg-[#8B5CF6] text-white">
            <UploadSimple size={16} className="mr-1" /> Import price list
          </Button>
        </div>
      </div>

      {stage === "extracting" && (
        <Card className="border-border shadow-none" data-testid="catalog-extracting">
          <CardContent className="py-8 text-center text-muted-foreground">
            Reading {current?.file_name} with the OCR engine — this takes a few seconds…
          </CardContent>
        </Card>
      )}

      {stage === "mapping" && current?.table && (
        <Card className="border-border shadow-none" data-testid="catalog-mapping">
          <CardContent className="py-5 space-y-4">
            <div>
              <p className="font-heading font-semibold">Map the spreadsheet columns</p>
              <p className="text-sm text-muted-foreground">{current.file_name} · {current.table.total_records} rows · remembered for next time</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {MAPPING_FIELDS.map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <Label>{label}</Label>
                  <Select value={mapping[key] || "none"} onValueChange={(v) => setMapping({ ...mapping, [key]: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid={`catalog-map-${key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {current.table.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            {current.table.sample?.length > 0 && (
              <div className="overflow-x-auto border border-border rounded-lg">
                <table className="w-full text-xs" data-testid="catalog-mapping-sample">
                  <thead><tr className="border-b text-left text-muted-foreground">
                    {current.table.columns.map((c) => <th key={c} className="px-3 py-2 font-medium">{c}</th>)}
                  </tr></thead>
                  <tbody>
                    {current.table.sample.map((rec, i) => (
                      <tr key={i} className="border-b last:border-0">
                        {current.table.columns.map((_, j) => <td key={j} className="px-3 py-1.5">{rec[j] || ""}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Button onClick={submitMapping} disabled={busy} data-testid="catalog-mapping-apply"
              className="bg-primary hover:bg-[#8B5CF6] text-white">{busy ? "Applying…" : "Apply mapping & match rows"}</Button>
          </CardContent>
        </Card>
      )}

      {stage === "review" && (
        <div className="space-y-4" data-testid="catalog-review">
          <Card className="border-border shadow-none">
            <CardContent className="py-4 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="catalog-select-all"
                  checked={rows.length > 0 && rows.every((r) => r.selected)}
                  onChange={(e) => toggleAll(e.target.checked)} /> Select all
              </label>
              <Button size="sm" variant="outline" data-testid="catalog-accept-matched" onClick={acceptMatched}>Accept all matched</Button>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" data-testid="catalog-accept-suggestions" onClick={acceptSuggestions}>Accept suggestions ≥</Button>
                <Input className="w-20" type="number" min="0" max="100" data-testid="catalog-threshold" value={threshold} onChange={(e) => setThreshold(e.target.value)} />%
              </div>
              <div className="flex items-center gap-2">
                <Select value={bulkCat || "none"} onValueChange={(v) => setBulkCat(v === "none" ? "" : v)}>
                  <SelectTrigger className="w-48" data-testid="catalog-bulk-category"><SelectValue placeholder="Category…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.department}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" data-testid="catalog-apply-category" onClick={applyCategory}>Assign to selected new items</Button>
              </div>
              <span className="text-sm text-muted-foreground ml-auto" data-testid="catalog-selected-count">{selectedCount} of {rows.length} rows will be confirmed</span>
            </CardContent>
          </Card>

          <div className="space-y-2" data-testid="catalog-review-rows">
            {rows.map((r, i) => (
              <div key={r.id} className={`border rounded-lg p-3 space-y-2 ${r.selected ? "border-border" : "border-border opacity-50"}`} data-testid={`catalog-row-${i}`}>
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={r.selected} data-testid={`catalog-row-select-${i}`}
                    onChange={(e) => patchRow(i, { selected: e.target.checked })} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{r.raw_description}</p>
                    <p className="text-xs text-muted-foreground">{r.list_price != null ? `${zar(r.list_price)} list` : "no price"}</p>
                  </div>
                  {(r.field_confidence || r.inferred_base_unit) && (
                    <Badge variant="outline" className="text-[#A78BFA] border-[#A78BFA]/40" data-testid={`catalog-row-ai-${i}`}
                      title="AI pre-filled pack/unit/category fields — amber fields are low-confidence guesses to verify">
                      AI pre-fill
                    </Badge>
                  )}
                  {stateChip(r)}
                </div>
                <MatchAssign line={r} index={i} stockItems={stockItems} idPrefix="catalog-row" alwaysPack
                  onPatch={patchRow} onNew={setNewItemFor} />
                {!r.stock_item_id && (
                  <label className="flex flex-wrap items-center gap-2 text-sm" data-testid={`catalog-row-createwrap-${i}`}>
                    <input type="checkbox" checked={r.create} data-testid={`catalog-row-create-${i}`}
                      onChange={(e) => patchRow(i, { create: e.target.checked })} />
                    Create new item:
                    <Input className="w-56" value={r.new_name} data-testid={`catalog-row-newname-${i}`}
                      onChange={(e) => patchRow(i, { new_name: e.target.value })} />
                    <Select value={r.new_unit} onValueChange={(v) => patchRow(i, { new_unit: v })}>
                      <SelectTrigger className={`w-24 ${lowConf(r, "base_unit") ? "border-[#F4A261]" : ""}`} data-testid={`catalog-row-newunit-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{BASE_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={r.new_category_id || "none"} onValueChange={(v) => patchRow(i, { new_category_id: v === "none" ? "" : v })}>
                      <SelectTrigger className={`w-44 ${lowConf(r, "category") ? "border-[#F4A261]" : ""}`} data-testid={`catalog-row-newcat-${i}`}><SelectValue placeholder="Category…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No category</SelectItem>
                        {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.department}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </label>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <Button onClick={confirm} disabled={busy} data-testid="catalog-confirm-btn"
              className="flex-1 bg-primary hover:bg-[#8B5CF6] text-white">
              <CheckCircle size={16} className="mr-1" /> {busy ? "Writing aliases…" : `Confirm ${selectedCount} rows (aliases only — no stock posted)`}
            </Button>
            <Button variant="outline" data-testid="catalog-cancel-btn" onClick={() => { setStage("idle"); setCurrent(null); setRows([]); }}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="catalog-history">
        <p className="font-heading font-semibold">Import history</p>
        {imports.length === 0 && <p className="text-sm text-muted-foreground">No imports yet.</p>}
        {imports.map((imp) => (
          <Card key={imp.id} className="border-border shadow-none" data-testid={`catalog-import-${imp.id}`}>
            <CardContent className="py-3 flex flex-wrap items-center gap-3">
              <a href={`${API_BASE}/catalog-imports/${imp.id}/file`} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 font-medium hover:text-primary" data-testid={`catalog-import-file-${imp.id}`}>
                <FileArrowDown size={15} /> {imp.file_name}
              </a>
              <span className="text-xs text-muted-foreground">{(imp.created_at || "").slice(0, 10)}</span>
              <Badge variant="outline" data-testid={`catalog-import-status-${imp.id}`}
                className={imp.status === "confirmed" ? "border-[#4A5D23]/40 text-[#A9C97E]" : imp.status === "failed" ? "text-[#F87171] border-[#B33939]/40" : "text-[#F4A261] border-[#F4A261]/40"}>
                {STATUS_LABELS[imp.status] || imp.status}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {imp.row_count} rows · {imp.matched_count} matched · {imp.created_count} new · {imp.updated_count} updated
              </span>
              {["mapping", "extracting", "pending_review"].includes(imp.status) && (
                <Button size="sm" variant="ghost" className="ml-auto" data-testid={`catalog-import-resume-${imp.id}`}
                  onClick={async () => { const { data } = await api.get(`/catalog-imports/${imp.id}`); handleImport(data); }}>
                  Resume
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={newItemFor !== null} onOpenChange={(o) => { if (!o) { setNewItemFor(null); setNewItem(EMPTY_ITEM); } }}>
        <DialogContent className="max-w-sm" data-testid="catalog-new-item-dialog">
          <DialogHeader><DialogTitle className="font-heading">New stock item</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Create a stock item for this catalog row</DialogDescription>
          <StockItemForm value={newItem} onChange={setNewItem} categories={categories} />
          <Button onClick={createItem} data-testid="catalog-new-item-save" className="w-full bg-primary hover:bg-[#8B5CF6] text-white mt-3">Create &amp; match</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
